// @client/pages/profile-manager.tsx
// Main Profile Manager Component - WITH DEEP-LINK INTEGRATION

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@client/hooks/use-toast";

// Deep-Link Helpers
import { parseDeepLink, validateDeepLinkParams } from "@client/utils/deep-link-helpers";

// Subcomponents
import { ProfileFilters } from "@client/components/profile-manager/ProfileFilters";
import { ProfileToolbar } from "@client/components/profile-manager/ProfileToolbar";
import { ProfileTable } from "@client/components/profile-manager/ProfileTable";
import { ProfilePagination } from "@client/components/profile-manager/ProfilePagination";
import { EditModal } from "@client/components/profile-manager/EditModal";

// Types - Profile Manager
import type {
  ProfileFilters as IProfileFilters,
  PaginationState,
  SortingState,
  ColumnVisibility,
  ProfileEditData,
  ProfileWithRelations,
} from "@shared/types";

// Types - Core Entities
import type { Category } from "@shared/types/core/category.types";
import type { List } from "@shared/types/core/list.types";
import type { User } from "@shared/types/core/user.types";
import type { ScoreRange } from "@shared/types/core/setting.types";

// Defaults & Helpers
import {
  DEFAULT_PROFILE_FILTERS,
  DEFAULT_PAGINATION,
  DEFAULT_SORTING,
  DEFAULT_COLUMN_VISIBILITY,
  loadColumnVisibility,
  saveColumnVisibility,
  loadPageLimit,
  savePageLimit,
  getBulkScope,
  validateDateRange,
} from "@shared/types/ui/profile-manager.types";

// Hooks
import {
  useProfilesQuery,
  useStaticDataQueries,
  useProfileMutations,
} from "@client/hooks/useProfilesQuery";

export default function ProfileManagerPage() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // ==================== DEEP-LINK: INITIAL STATE FROM URL ====================
  
  // Initialize filters from URL on mount
  const initialFilters = useMemo(() => {
    const urlFilters = parseDeepLink(searchParams);
    
    // Validate URL parameters and show warnings if invalid
    const warnings = validateDeepLinkParams(searchParams);
    if (warnings.length > 0) {
      console.warn('[ProfileManager] Invalid URL parameters:', warnings);
      // Optional: Show toast for invalid params
      // toast({
      //   title: 'Invalid URL parameters',
      //   description: warnings.join(', '),
      //   variant: 'destructive'
      // });
    }
    
    return {
      ...DEFAULT_PROFILE_FILTERS,
      ...urlFilters  // URL überschreibt Defaults
    };
  }, []); // Empty deps = nur beim Mount

  // ==================== STATE ====================

  // Core State
  const [filters, setFilters] = useState<IProfileFilters>(initialFilters);
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    page: 1,
    limit: loadPageLimit()
  }));
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [selectedProfiles, setSelectedProfiles] = useState<number[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => 
    loadColumnVisibility()
  );

  // Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState<'single' | 'bulk'>('single');
  const [editingProfile, setEditingProfile] = useState<ProfileWithRelations | null>(null);

  // Inline Editing State
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);

  // ==================== DEEP-LINK: SYNC FILTERS TO URL ====================
  
  useEffect(() => {
    const params = new URLSearchParams();
    
    // Text Search
    if (filters.search && filters.search.trim()) {
      params.set('search', filters.search.trim());
    }

    // Multi-List Filter (NEU)
    if (filters.listIds && filters.listIds.length > 0) {
      params.set('listIds', filters.listIds.join(','));
    }
    
    // List Filter
    if (filters.listId && filters.listId !== 'all') {
      params.set('listId', filters.listId.toString());
    }
    
    // Category Filter (Multi-Select)
    if (filters.categoryIds && filters.categoryIds.length > 0) {
      params.set('categoryIds', filters.categoryIds.join(','));
    }
    
    // Score Range
    if (filters.scoreRange && filters.scoreRange !== 'all') {
      params.set('scoreRange', filters.scoreRange);
    }
    
    // Created By User
    if (filters.createdBy) {
      params.set('createdBy', filters.createdBy.toString());
    }
    
    // After Date Filter
    if (filters.afterField && filters.afterDate) {
      params.set('afterField', filters.afterField);
      params.set('afterDate', filters.afterDate);
    }
    
    // Before Date Filter
    if (filters.beforeField && filters.beforeDate) {
      params.set('beforeField', filters.beforeField);
      params.set('beforeDate', filters.beforeDate);
    }
    
    // Boolean Markers
    if (filters.isExported !== undefined) {
      params.set('isExported', filters.isExported.toString());
    }
    if (filters.isActive !== undefined) {
      params.set('isActive', filters.isActive.toString());
    }
    if (filters.isBlocked !== undefined) {
      params.set('isBlocked', filters.isBlocked.toString());
    }
    if (filters.isRegistered !== undefined) {
      params.set('isRegistered', filters.isRegistered.toString());
    }
    if (filters.hasCategories !== undefined) {
      params.set('hasCategories', filters.hasCategories.toString());
    }
    if (filters.hasLists !== undefined) {
      params.set('hasLists', filters.hasLists.toString());
    }
    if (filters.hasEmail !== undefined) {
      params.set('hasEmail', filters.hasEmail.toString());
    }
    
    // Update URL (replace = no history entry per filter change)
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // ==================== DATA FETCHING ====================

  // Static Data (runs once)
  const {
    lists,
    categories,
    users,
    scoreRanges,
    isLoading: staticLoading,
    isError: staticError
  } = useStaticDataQueries();

  // Profile Search (runs on filters/pagination/sorting change)
  const {
    data: searchResult,
    isLoading: profilesLoading,
    isError: profilesError,
    refetch: refetchProfiles
  } = useProfilesQuery(filters, pagination, sorting, {
    enabled: !staticLoading && !staticError
  });

  // Mutations
  const {
    profileUpdate,
    relations,
    bulkUpdate,
    bulkDelete,
    bulkExport,
    bulkAction,
    isLoading: mutationLoading
  } = useProfileMutations();

  // ==================== COMPUTED VALUES ====================

  const profiles = searchResult?.profiles || [];
  const paginationMeta = searchResult?.pagination;
  const totalProfiles = paginationMeta?.total || 0;

  // Bulk Scope
  const bulkScope = useMemo(() => 
    getBulkScope(selectedProfiles.length, totalProfiles),
    [selectedProfiles.length, totalProfiles]
  );

  // Active Filter Count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.listId && filters.listId !== 'all') count++;
    if (filters.categoryIds && filters.categoryIds.length > 0) count++;
    if (filters.scoreRange) count++;
    if (filters.createdBy) count++;
    if (filters.afterDate) count++;
    if (filters.beforeDate) count++;
    if (filters.isExported) count++;
    if (filters.isActive) count++;
    if (filters.isBlocked) count++;
    if (filters.isRegistered) count++;
    if (filters.hasCategories !== undefined) count++;
    if (filters.hasLists) count++;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFilterCount > 0;

  // ==================== PERSISTENCE ====================

  // Save column visibility on change
  useEffect(() => {
    saveColumnVisibility(columnVisibility);
  }, [columnVisibility]);

  // ==================== EVENT HANDLERS ====================

  // Filter Change Handler
  const handleFilterChange = useCallback((key: keyof IProfileFilters, value: any) => {
    // Date range validation
    if (key === 'afterDate' || key === 'beforeDate') {
      const newFilters = { ...filters, [key]: value };
      const validation = validateDateRange(newFilters.afterDate, newFilters.beforeDate);
      
      if (!validation.isValid) {
        toast({
          title: 'Invalid date range',
          description: validation.error,
          variant: 'destructive'
        });
        return;
      }
    }

    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1
    setSelectedProfiles([]); // Clear selection
  }, [filters, toast]);

  // Clear All Filters
  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_PROFILE_FILTERS);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedProfiles([]);
  }, []);

  // Refresh/Search
  const handleRefresh = useCallback(() => {
    refetchProfiles();
    toast({
      title: 'Refreshing profiles...',
      duration: 2000
    });
  }, [refetchProfiles, toast]);

  // Pagination Handlers
  const handlePageChange = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
    setSelectedProfiles([]); // Clear selection on page change
  }, []);

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ page: 1, limit });
    savePageLimit(limit);
    setSelectedProfiles([]);
  }, []);

  // Sorting Handler
  const handleSortingChange = useCallback((field: string, order?: 'asc' | 'desc') => {
    setSorting(prev => ({
      field: field as SortingState['field'],
      order: order || (prev.field === field && prev.order === 'asc' ? 'desc' : 'asc')
    }));
  }, []);

  // Column Visibility Handler
  const handleColumnVisibilityChange = useCallback((visibility: ColumnVisibility) => {
    setColumnVisibility(visibility);
  }, []);

  // Selection Handlers
  const handleProfileSelection = useCallback((profileId: number) => {
    setSelectedProfiles(prev => {
      if (prev.includes(profileId)) {
        return prev.filter(id => id !== profileId);
      } else {
        return [...prev, profileId];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedProfiles.length === profiles.length && profiles.length > 0) {
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles(profiles.map(p => p.id));
    }
  }, [profiles, selectedProfiles]);

  // ✅ FIXED: Quick Edit Handler with proper Relations API
  const handleQuickEdit = useCallback(async (
    profileId: number,
    field: string,
    value: any
  ) => {
  try {
    if (field === 'categories') {
      // ✅ FIXED: Use proper relations mutation with diff
      
      // Find current profile to get existing categories
      /*
      const profile = profiles.find(p => p.id === profileId);
      const currentIds = profile?.categories?.map(c => c.categoryId) || [];
      const newIds = value as number[];
      
      console.log('[ProfileManager] Quick edit categories:', {
        profileId,
        currentIds,
        newIds
      });
      
      // Call mutation with old/new IDs
      await relations.mutateAsync({
        profileId,
        type: 'categories',
        oldIds: currentIds,
        newIds: newIds
      });
      */
      // Success toast handled by mutation hook
      
    } else {
      // Standard field update (score, gender, industry, etc.)
      await profileUpdate.mutateAsync({
        profileId,
        updates: { [field]: value }
      });
    }
  } catch (error: any) {
    console.error('[ProfileManager] Quick edit failed:', error);
    
    // Error handling done by mutation hooks
    // Additional toast only if needed
    toast({
      title: 'Update failed',
      description: error?.message || 'Please try again',
      variant: 'destructive'
    });
  }
}, [profiles, profileUpdate, relations, toast]);

  // Edit Modal Handlers
  const handleOpenEditModal = useCallback((profile?: ProfileWithRelations) => {
    if (profile) {
      setEditMode('single');
      setEditingProfile(profile);
    } else {
      setEditMode('bulk');
      setEditingProfile(null);
    }
    setShowEditModal(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditMode('single');
    setEditingProfile(null);
  }, []);

  const handleSaveEdit = useCallback(async (data: ProfileEditData) => {
    try {
      if (editMode === 'single' && editingProfile) {
        // Single profile update
        await profileUpdate.mutateAsync({
          profileId: editingProfile.id,
          updates: data
        });
      } else if (editMode === 'bulk') {
        // Bulk update
        const request = {
          mode: bulkScope.type,
          profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
          filters: bulkScope.type === 'filtered' ? filters : undefined,
          pagination: bulkScope.type === 'filtered' ? pagination : undefined,
          sorting: bulkScope.type === 'filtered' ? sorting : undefined,
          updates: data
        };

        await bulkUpdate.mutateAsync(request);
        setSelectedProfiles([]);
      }

      handleCloseEditModal();
      // Success toast handled by mutation hooks
    } catch (error: any) {
      toast({
        title: 'Save failed',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [
    editMode,
    editingProfile,
    bulkScope,
    selectedProfiles,
    filters,
    pagination,
    sorting,
    profileUpdate,
    bulkUpdate,
    handleCloseEditModal,
    toast
  ]);

  // Toolbar Action Handlers
  const handleCategoryUpdate = useCallback(async (categoryId: number) => {
    const request = {
      mode: bulkScope.type,
      profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
      filters: bulkScope.type === 'filtered' ? filters : undefined,
      pagination: bulkScope.type === 'filtered' ? pagination : undefined,
      sorting: bulkScope.type === 'filtered' ? sorting : undefined,
      updates: { categoryId }
    };

    try {
      await bulkUpdate.mutateAsync(request);
      setSelectedProfiles([]);
      toast({
        title: 'Category added',
        description: `Added category to ${bulkScope.count} profile(s)`
      });
    } catch (error: any) {
      toast({
        title: 'Failed to add category',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [bulkScope, selectedProfiles, filters, pagination, sorting, bulkUpdate, toast]);

  const handleListUpdate = useCallback(async (listId: number) => {
    const request = {
      mode: bulkScope.type,
      profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
      filters: bulkScope.type === 'filtered' ? filters : undefined,
      pagination: bulkScope.type === 'filtered' ? pagination : undefined,
      sorting: bulkScope.type === 'filtered' ? sorting : undefined,
      updates: { listId }
    };

    try {
      await bulkUpdate.mutateAsync(request);
      setSelectedProfiles([]);
      toast({
        title: 'Added to list',
        description: `Added ${bulkScope.count} profile(s) to list`
      });
    } catch (error: any) {
      toast({
        title: 'Failed to add to list',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [bulkScope, selectedProfiles, filters, pagination, sorting, bulkUpdate, toast]);

// ✅ V2.1: Multi-Select Handlers für ProfileToolbar V2.0
  
  const handleCategoriesUpdate = useCallback(async (categoryIds: number[]) => {
    const request = {
      mode: bulkScope.type,
      profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
      filters: bulkScope.type === 'filtered' ? filters : undefined,
      pagination: bulkScope.type === 'filtered' ? pagination : undefined,
      sorting: bulkScope.type === 'filtered' ? sorting : undefined,
      updates: { categoryIds }  // Multi-Select
    };

    try {
      await bulkUpdate.mutateAsync(request);
      setSelectedProfiles([]);
      toast({
        title: 'Categories added',
        description: `Added ${categoryIds.length} category(ies) to ${bulkScope.count} profile(s)`
      });
    } catch (error: any) {
      toast({
        title: 'Failed to add categories',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [bulkScope, selectedProfiles, filters, pagination, sorting, bulkUpdate, toast]);

  const handleListsUpdate = useCallback(async (listIds: number[]) => {
    const request = {
      mode: bulkScope.type,
      profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
      filters: bulkScope.type === 'filtered' ? filters : undefined,
      pagination: bulkScope.type === 'filtered' ? pagination : undefined,
      sorting: bulkScope.type === 'filtered' ? sorting : undefined,
      updates: { listIds }  // Multi-Select
    };

    try {
      await bulkUpdate.mutateAsync(request);
      setSelectedProfiles([]);
      toast({
        title: 'Added to lists',
        description: `Added ${bulkScope.count} profile(s) to ${listIds.length} list(s)`
      });
    } catch (error: any) {
      toast({
        title: 'Failed to add to lists',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [bulkScope, selectedProfiles, filters, pagination, sorting, bulkUpdate, toast]);

  const handleMarkerUpdate = useCallback(async (marker: string, value: boolean) => {
    const request = {
      mode: bulkScope.type,
      profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
      filters: bulkScope.type === 'filtered' ? filters : undefined,
      pagination: bulkScope.type === 'filtered' ? pagination : undefined,
      sorting: bulkScope.type === 'filtered' ? sorting : undefined,
      updates: { [marker]: value }
    };

    try {
      await bulkUpdate.mutateAsync(request);
      setSelectedProfiles([]);
      toast({
        title: 'Marker updated',
        description: `Updated ${marker} for ${bulkScope.count} profile(s)`
      });
    } catch (error: any) {
      toast({
        title: 'Failed to update marker',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [bulkScope, selectedProfiles, filters, pagination, sorting, bulkUpdate, toast]);

  const handleAction = useCallback(async (action: string) => {
    const request = {
      mode: bulkScope.type,
      profileIds: bulkScope.type === 'selected' ? selectedProfiles : undefined,
      filters: bulkScope.type === 'filtered' ? filters : undefined,
      pagination: bulkScope.type === 'filtered' ? pagination : undefined,
      sorting: bulkScope.type === 'filtered' ? sorting : undefined,
      format: 'csv' as const,
      includeRelations: true,
      ...(action === 'delete' && { confirmed: true })
    };

    try {
      switch (action) {
        case 'export':
          await bulkExport.mutateAsync(request);
          toast({
            title: 'Export started',
            description: `Exporting ${bulkScope.count} profile(s)`
          });
          break;

        case 'delete':
          await bulkDelete.mutateAsync(request);
          setSelectedProfiles([]);
          toast({
            title: 'Profiles deleted',
            description: `Deleted ${bulkScope.count} profile(s)`
          });
          break;

        case 'check-crmid':
        case 'generate-industry':
        case 'generate-email':
        case 'generate-gender':
          const actionRequest = {
            ...request,
            action
          }
          await bulkAction.mutateAsync(actionRequest);
          toast({
            title: `Action ${action} started`,
            description: `Running on ${bulkScope.count} profile(s)`
          });
          break;

        default:
          console.warn('[ProfileManager] Unknown action:', action);
      }
    } catch (error: any) {
      toast({
        title: 'Action failed',
        description: error?.message || 'Please try again',
        variant: 'destructive'
      });
    }
  }, [
    bulkScope,
    selectedProfiles,
    filters,
    pagination,
    sorting,
    bulkExport,
    bulkDelete,
    bulkAction,
    toast
  ]);

  // ==================== LOADING & ERROR STATES ====================

  if (staticError) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <h2 className="text-lg font-semibold text-destructive mb-2">
            Failed to load static data
          </h2>
          <p className="text-sm text-muted-foreground">
            Unable to load lists, categories, or users. Please refresh the page.
          </p>
        </div>
      </div>
    );
  }

  if (staticLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading profile manager...</p>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Profile Manager</h1>
          <p className="text-muted-foreground mt-1">
            Manage and organize LinkedIn profiles
          </p>
        </div>
      </div>

      {/* Filters */}
      <ProfileFilters
        filters={filters}
        lists={lists}
        categories={categories}
        users={users}
        scoreRanges={scoreRanges}
        hasActiveFilters={hasActiveFilters}
        activeFilterCount={activeFilterCount}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        onRefresh={handleRefresh}
        loading={profilesLoading}
      />

      {/* Toolbar */}
      {totalProfiles > 0 && (
        <ProfileToolbar
          bulkScope={bulkScope}
          lists={lists}
          categories={categories}
          onCategoriesUpdate={handleCategoriesUpdate}
          onListsUpdate={handleListsUpdate}
          onCategoryUpdate={handleCategoryUpdate}
          onListUpdate={handleListUpdate}
          onMarkerUpdate={handleMarkerUpdate}
          onEditFields={() => handleOpenEditModal()}
          onAction={handleAction}
          loading={mutationLoading}
          disabled={bulkScope.count === 0}
        />
      )}

      {/* Table */}
      <ProfileTable
        profiles={profiles}
        selectedProfiles={selectedProfiles}
        columnVisibility={columnVisibility}
        sorting={sorting}
        categories={categories}
        lists={lists}
        onProfileSelection={handleProfileSelection}
        onSelectAll={handleSelectAll}
        onSortingChange={handleSortingChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        onEditProfile={handleOpenEditModal}
        onQuickEdit={handleQuickEdit}
        editingCategory={editingCategoryId}
        onEditCategory={setEditingCategoryId}
        loading={profilesLoading}
      />

      {/* Pagination */}
      {paginationMeta && paginationMeta.totalPages > 1 && (
        <ProfilePagination
          pagination={paginationMeta}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
        />
      )}

      {/* Edit Modal */}
      <EditModal
        open={showEditModal}
        onOpenChange={handleCloseEditModal}
        mode={editMode}
        scopeLabel={editMode === 'bulk' ? bulkScope.label : editingProfile?.name || 'Profile'}
        initialData={
          editMode === 'single' && editingProfile
            ? {
                name: editingProfile.name || '',
                title: editingProfile.title || '',
                company: editingProfile.company || '',
                location: editingProfile.location || '',
                email: editingProfile.email || '',
                gender: editingProfile.gender || '',
                industry: editingProfile.industry || '',
                score: editingProfile.score || undefined
              }
            : {}
        }
        onSubmit={handleSaveEdit}
        loading={profileUpdate.isPending || bulkUpdate.isPending}
        profile={editingProfile || undefined}
        affectedCount={editMode === 'bulk' ? bulkScope.count : undefined}
      />

      {/* Error State */}
      {profilesError && (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <p className="text-sm text-destructive">
            Failed to load profiles. Please try refreshing.
          </p>
        </div>
      )}
    </div>
  );
}